import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  type LogEntry,
  type LogLevel,
  type LogCategory,
  subscribe,
  getMemoryLogs,
  getOfflineCount,
  fetchLogsFromDb,
  fetchLogSources,
  type LogsFilter,
} from '../lib/logger';

const LEVEL_COLORS: Record<LogLevel, string> = {
  debug: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-100 text-blue-700',
  warn: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
};

const CATEGORY_LABELS: Record<LogCategory, string> = {
  functional: 'Fonctionnel',
  technical: 'Technique',
  error: 'Erreur',
};

const PAGE_SIZE = 50;

const Logs: React.FC = () => {
  const [tab, setTab] = useState<'realtime' | 'history'>('realtime');

  // Realtime state
  const [realtimeLogs, setRealtimeLogs] = useState<LogEntry[]>([...getMemoryLogs()].reverse());
  const [rtLevelFilter, setRtLevelFilter] = useState<string>('all');
  const [rtCatFilter, setRtCatFilter] = useState<string>('all');
  const [rtSourceFilter, setRtSourceFilter] = useState<string>('all');
  const [rtSearch, setRtSearch] = useState('');
  const [autoscroll, setAutoscroll] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  // History state
  const [historyLogs, setHistoryLogs] = useState<LogEntry[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hLevelFilter, setHLevelFilter] = useState<string>('all');
  const [hCatFilter, setHCatFilter] = useState<string>('all');
  const [hSourceFilter, setHSourceFilter] = useState<string>('all');
  const [hSearch, setHSearch] = useState('');
  const [sources, setSources] = useState<string[]>([]);

  // Detail modal
  const [detailEntry, setDetailEntry] = useState<LogEntry | null>(null);

  // Offline indicator
  const [offlineCount, setOfflineCount] = useState(getOfflineCount());
  useEffect(() => {
    const interval = setInterval(() => setOfflineCount(getOfflineCount()), 3000);
    return () => clearInterval(interval);
  }, []);

  // Realtime subscription
  useEffect(() => {
    const unsub = subscribe((entry) => {
      setRealtimeLogs((prev) => [entry, ...prev].slice(0, 500));
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (autoscroll && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [realtimeLogs, autoscroll]);

  // Load sources for dropdown
  useEffect(() => {
    fetchLogSources().then(setSources).catch(() => {});
  }, []);

  // History loader
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const filter: LogsFilter = {
        page: historyPage,
        pageSize: PAGE_SIZE,
      };
      if (hLevelFilter !== 'all') filter.level = hLevelFilter as LogLevel;
      if (hCatFilter !== 'all') filter.category = hCatFilter as LogCategory;
      if (hSourceFilter !== 'all') filter.source = hSourceFilter;
      if (hSearch) filter.search = hSearch;

      const result = await fetchLogsFromDb(filter);
      setHistoryLogs(result.entries);
      setHistoryTotal(result.total);
    } catch {
      setHistoryLogs([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, hLevelFilter, hCatFilter, hSourceFilter, hSearch]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // Filter realtime logs
  const filteredRt = realtimeLogs.filter((e) => {
    if (rtLevelFilter !== 'all' && e.level !== rtLevelFilter) return false;
    if (rtCatFilter !== 'all' && e.category !== rtCatFilter) return false;
    if (rtSourceFilter !== 'all' && e.source !== rtSourceFilter) return false;
    if (rtSearch) {
      const q = rtSearch.toLowerCase();
      if (!e.action.toLowerCase().includes(q) && !JSON.stringify(e.details).toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const rtSources = [...new Set(realtimeLogs.map((e) => e.source))].sort();
  const allSources = [...new Set([...rtSources, ...sources])].sort();

  const exportLogs = () => {
    const data = tab === 'realtime' ? filteredRt : historyLogs;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${new Date().toISOString().slice(0, 19)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalHistoryPages = Math.ceil(historyTotal / PAGE_SIZE);

  const renderRow = (entry: LogEntry) => (
    <tr
      key={entry.id}
      className="hover:bg-gray-50/50 transition-colors cursor-pointer"
      onClick={() => setDetailEntry(entry)}
    >
      <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
        {entry.createdAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </td>
      <td className="px-4 py-2.5">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${LEVEL_COLORS[entry.level]}`}>
          {entry.level.toUpperCase()}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <span className="text-xs text-gray-500">{CATEGORY_LABELS[entry.category]}</span>
      </td>
      <td className="px-4 py-2.5 text-xs font-medium text-gray-600">{entry.source}</td>
      <td className="px-4 py-2.5 text-xs text-gray-800 font-mono">{entry.action}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate">
        {Object.keys(entry.details).length > 0
          ? JSON.stringify(entry.details).slice(0, 80)
          : '-'}
      </td>
    </tr>
  );

  const filterBar = (
    levelFilter: string, setLevel: (v: string) => void,
    catFilter: string, setCat: (v: string) => void,
    sourceFilter: string, setSource: (v: string) => void,
    search: string, setSearch: (v: string) => void,
    sourcesArr: string[],
  ) => (
    <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/50">
      <select
        value={levelFilter}
        onChange={(e) => setLevel(e.target.value)}
        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
      >
        <option value="all">Tous niveaux</option>
        <option value="debug">Debug</option>
        <option value="info">Info</option>
        <option value="warn">Warning</option>
        <option value="error">Error</option>
      </select>

      <select
        value={catFilter}
        onChange={(e) => setCat(e.target.value)}
        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
      >
        <option value="all">Toutes categories</option>
        <option value="functional">Fonctionnel</option>
        <option value="technical">Technique</option>
        <option value="error">Erreur</option>
      </select>

      <select
        value={sourceFilter}
        onChange={(e) => setSource(e.target.value)}
        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
      >
        <option value="all">Toutes sources</option>
        {sourcesArr.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      <div className="relative flex-1 min-w-[180px] max-w-xs">
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-3 pr-4 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Logs</h1>
          <p className="text-sm text-gray-500 mt-1">Journal centralise des evenements, erreurs et retours API</p>
        </div>
        <div className="flex items-center gap-3">
          {offlineCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-xs font-medium text-amber-700">
                {offlineCount} log{offlineCount > 1 ? 's' : ''} en attente (offline)
              </span>
            </div>
          )}
          <button
            onClick={exportLogs}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Exporter JSON
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('realtime')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            tab === 'realtime' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Temps reel ({filteredRt.length})
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            tab === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Historique ({historyTotal})
        </button>
      </div>

      {/* Realtime */}
      {tab === 'realtime' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {filterBar(rtLevelFilter, setRtLevelFilter, rtCatFilter, setRtCatFilter, rtSourceFilter, setRtSourceFilter, rtSearch, setRtSearch, rtSources)}

          <div className="flex items-center justify-between px-5 py-2 border-b border-gray-100">
            <span className="text-xs text-gray-400">{filteredRt.length} evenements en session</span>
            <label className="flex items-center gap-2 text-xs text-gray-500">
              <input
                type="checkbox"
                checked={autoscroll}
                onChange={(e) => setAutoscroll(e.target.checked)}
                className="rounded"
              />
              Auto-scroll
            </label>
          </div>

          <div ref={listRef} className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Heure</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Niveau</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Categorie</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredRt.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-400">
                      Aucun evenement pour ces filtres
                    </td>
                  </tr>
                ) : (
                  filteredRt.map(renderRow)
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {filterBar(hLevelFilter, setHLevelFilter, hCatFilter, setHCatFilter, hSourceFilter, setHSourceFilter, hSearch, setHSearch, allSources)}

          {historyLoading ? (
            <div className="px-5 py-12 text-center text-sm text-gray-400">Chargement...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Niveau</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Categorie</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {historyLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-400">
                          Aucun log en base pour ces filtres
                        </td>
                      </tr>
                    ) : (
                      historyLogs.map((entry) => (
                        <tr
                          key={entry.id}
                          className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                          onClick={() => setDetailEntry(entry)}
                        >
                          <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                            {entry.createdAt.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${LEVEL_COLORS[entry.level]}`}>
                              {entry.level.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs text-gray-500">{CATEGORY_LABELS[entry.category]}</span>
                          </td>
                          <td className="px-4 py-2.5 text-xs font-medium text-gray-600">{entry.source}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-800 font-mono">{entry.action}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate">
                            {Object.keys(entry.details).length > 0
                              ? JSON.stringify(entry.details).slice(0, 80)
                              : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalHistoryPages > 1 && (
                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Page {historyPage + 1} sur {totalHistoryPages} ({historyTotal} resultats)
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                      disabled={historyPage === 0}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                    >
                      Precedent
                    </button>
                    <button
                      onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages - 1, p + 1))}
                      disabled={historyPage >= totalHistoryPages - 1}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {detailEntry && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setDetailEntry(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detail du log</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {detailEntry.createdAt.toLocaleString('fr-FR')}
                </p>
              </div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${LEVEL_COLORS[detailEntry.level]}`}>
                {detailEntry.level.toUpperCase()}
              </span>
            </div>

            <div className="px-6 py-4 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase font-semibold">Categorie</label>
                  <p className="text-sm text-gray-800">{CATEGORY_LABELS[detailEntry.category]}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase font-semibold">Source</label>
                  <p className="text-sm text-gray-800 font-mono">{detailEntry.source}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-400 uppercase font-semibold">Action</label>
                  <p className="text-sm text-gray-800 font-mono">{detailEntry.action}</p>
                </div>
                {detailEntry.userId && (
                  <div className="col-span-2">
                    <label className="text-xs text-gray-400 uppercase font-semibold">User ID</label>
                    <p className="text-sm text-gray-800 font-mono">{detailEntry.userId}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase font-semibold">Details (JSON)</label>
                <pre className="mt-1 p-3 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto max-h-60">
                  {JSON.stringify(detailEntry.details, null, 2)}
                </pre>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setDetailEntry(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
